/**
 * AI Settings React Query Hooks
 *
 * Replaces useAISettingsStore for server-side AI configuration.
 * Local form state is managed by the consumer component.
 *
 * For non-React contexts, use getAiSettingsFromCache().
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { graphqlFetch } from '../database/graphqlClient';
import { queryKeys } from './queryKeys';
import { queryClient } from './queryClient';

// =============================================================================
// TYPES (re-exported from old store, now owned here)
// =============================================================================

export type AIProvider = 'anthropic' | 'openai' | 'disabled';
export type WhisperModel = 'tiny' | 'base' | 'small' | 'medium' | 'large';
export type VoiceLanguage = 'auto' | 'it' | 'en';
export type ConversationRetention = 7 | 30 | 90 | -1;

export interface AIFeatures {
  homeChat: boolean;
  projectSuggestions: boolean;
  projectTemplates: boolean;
  hubReplySuggestions: boolean;
  hubSummary: boolean;
  accountingCategorization: boolean;
  accountingForecasts: boolean;
}

export interface AISettings {
  settingsId: string | null;
  provider: AIProvider;
  cloudApiKey: string;
  chatModel: string;
  embeddingsModel: string;
  temperature: number;
  maxTokens: number;
  topP: number;
  contextWindow: number;
  features: AIFeatures;
  voiceEnabled: boolean;
  whisperModel: WhisperModel;
  voiceLanguage: VoiceLanguage;
  sendAnonymousData: boolean;
  saveConversations: boolean;
  conversationRetention: ConversationRetention;
  systemPrompt: string;
  debugMode: boolean;
}

// =============================================================================
// DEFAULTS
// =============================================================================

const DEFAULT_SYSTEM_PROMPT = `Sei un assistente AI integrato in un'applicazione business.
Aiuti l'utente con:
- Gestione progetti e task
- Comunicazioni (email, messaggi)
- Contabilità e fatturazione
- Pianificazione calendario

Rispondi sempre in italiano in modo professionale e conciso.`;

const DEFAULT_FEATURES: AIFeatures = {
  homeChat: true,
  projectSuggestions: true,
  projectTemplates: true,
  hubReplySuggestions: true,
  hubSummary: true,
  accountingCategorization: true,
  accountingForecasts: false,
};

const MODEL_CONTEXT_WINDOWS: Record<string, number> = {
  'claude-sonnet-4-5-20250929': 200000,
  'claude-haiku-4-5-20251001': 200000,
  'gpt-4o': 128000,
  'gpt-4o-mini': 128000,
  'llama-3.3-70b-versatile': 131072,
  'llama-3.1-8b-instant': 131072,
};

function getContextWindow(modelName: string): number {
  if (MODEL_CONTEXT_WINDOWS[modelName]) {
    return MODEL_CONTEXT_WINDOWS[modelName];
  }
  const baseName = modelName.split(':')[0];
  for (const [key, value] of Object.entries(MODEL_CONTEXT_WINDOWS)) {
    if (key.startsWith(baseName)) {
      return value;
    }
  }
  return 4096;
}

export const DEFAULT_AI_SETTINGS: AISettings = {
  settingsId: null,
  provider: 'anthropic',
  cloudApiKey: '',
  chatModel: 'claude-sonnet-4-5-20250929',
  embeddingsModel: '',
  temperature: 0.7,
  maxTokens: 2048,
  topP: 0.9,
  contextWindow: 8192,
  features: { ...DEFAULT_FEATURES },
  voiceEnabled: false,
  whisperModel: 'base',
  voiceLanguage: 'auto',
  sendAnonymousData: false,
  saveConversations: true,
  conversationRetention: 30,
  systemPrompt: DEFAULT_SYSTEM_PROMPT,
  debugMode: false,
};

// =============================================================================
// GRAPHQL
// =============================================================================

const AI_SETTINGS_QUERY = `
  query GetAISettings($userId: String) {
    aiSettings(userId: $userId) {
      id
      userId
      provider
      cloudApiKey
      chatModel
      embeddingsModel
      temperature
      maxTokens
      topP
      features {
        homeChat
        projectSuggestions
        projectTemplates
        hubReplySuggestions
        hubSummary
        accountingCategorization
        accountingForecasts
      }
      voiceEnabled
      whisperModel
      voiceLanguage
      sendAnonymousData
      saveConversations
      conversationRetention
      systemPrompt
      debugMode
      createdAt
      updatedAt
    }
  }
`;

const SAVE_AI_SETTINGS_MUTATION = `
  mutation SaveAISettings($input: AISettingsInput!, $userId: String) {
    saveAiSettings(input: $input, userId: $userId) {
      id
      provider
      chatModel
      updatedAt
    }
  }
`;

// =============================================================================
// MAPPERS
// =============================================================================

function mapGraphQLSettings(s: any): AISettings {
  return {
    settingsId: s.id,
    provider: (s.provider === 'ollama' ? 'anthropic' : s.provider) as AIProvider,
    cloudApiKey: s.cloudApiKey || '',
    chatModel: s.chatModel,
    embeddingsModel: s.embeddingsModel,
    temperature: s.temperature,
    maxTokens: s.maxTokens,
    topP: s.topP,
    contextWindow: getContextWindow(s.chatModel),
    features: {
      homeChat: s.features.homeChat,
      projectSuggestions: s.features.projectSuggestions,
      projectTemplates: s.features.projectTemplates,
      hubReplySuggestions: s.features.hubReplySuggestions,
      hubSummary: s.features.hubSummary,
      accountingCategorization: s.features.accountingCategorization,
      accountingForecasts: s.features.accountingForecasts,
    },
    voiceEnabled: s.voiceEnabled,
    whisperModel: s.whisperModel as WhisperModel,
    voiceLanguage: s.voiceLanguage as VoiceLanguage,
    sendAnonymousData: s.sendAnonymousData,
    saveConversations: s.saveConversations,
    conversationRetention: s.conversationRetention as ConversationRetention,
    systemPrompt: s.systemPrompt,
    debugMode: s.debugMode,
  };
}

function settingsToInput(settings: AISettings) {
  return {
    provider: settings.provider,
    cloud_api_key: settings.cloudApiKey || null,
    chat_model: settings.chatModel,
    embeddings_model: settings.embeddingsModel,
    temperature: settings.temperature,
    max_tokens: settings.maxTokens,
    top_p: settings.topP,
    features: {
      home_chat: settings.features.homeChat,
      project_suggestions: settings.features.projectSuggestions,
      project_templates: settings.features.projectTemplates,
      hub_reply_suggestions: settings.features.hubReplySuggestions,
      hub_summary: settings.features.hubSummary,
      accounting_categorization: settings.features.accountingCategorization,
      accounting_forecasts: settings.features.accountingForecasts,
    },
    voice_enabled: settings.voiceEnabled,
    whisper_model: settings.whisperModel,
    voice_language: settings.voiceLanguage,
    send_anonymous_data: settings.sendAnonymousData,
    save_conversations: settings.saveConversations,
    conversation_retention: settings.conversationRetention,
    system_prompt: settings.systemPrompt,
    debug_mode: settings.debugMode,
  };
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Fetch AI settings from the server.
 */
export function useAiSettings() {
  return useQuery({
    queryKey: queryKeys.aiSettings.settings(),
    queryFn: async () => {
      const response = await graphqlFetch<{ aiSettings: any }>(AI_SETTINGS_QUERY);
      if (response.errors) {
        console.error('GraphQL errors:', response.errors);
      }
      if (response.data?.aiSettings) {
        return mapGraphQLSettings(response.data.aiSettings);
      }
      return DEFAULT_AI_SETTINGS;
    },
    staleTime: 30 * 60_000, // settings rarely change
  });
}

/**
 * Save AI settings to the server.
 */
export function useSaveAiSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (settings: AISettings) => {
      const input = settingsToInput(settings);
      const response = await graphqlFetch<{ saveAiSettings: any }>(
        SAVE_AI_SETTINGS_MUTATION,
        { input }
      );
      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }
      if (response.data?.saveAiSettings) {
        // Update the cache with the full settings (server only returns partial)
        const updated = {
          ...settings,
          settingsId: response.data.saveAiSettings.id,
        };
        return updated;
      }
      throw new Error('Failed to save AI settings');
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.aiSettings.settings(), data);
    },
  });
}

// =============================================================================
// NON-REACT ACCESS (for services and utilities)
// =============================================================================

/**
 * Read AI settings from the react-query cache.
 * For use in non-React code (services, utilities).
 * Returns DEFAULT_AI_SETTINGS if cache is empty.
 */
export function getAiSettingsFromCache(): AISettings {
  return queryClient.getQueryData<AISettings>(queryKeys.aiSettings.settings()) ?? DEFAULT_AI_SETTINGS;
}

// Re-export context window helper for external use
export { getContextWindow };
